import { isGoSpace, trimGoSpace } from './goText'

const EXPLICIT_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//
const SCHEME_PREFIX = /^[a-zA-Z][a-zA-Z0-9+.-]*:(.*)$/
const PORT_SUFFIX = /^[0-9]+([/?#].*)?$/
const OPTIONAL_PORT = /^:[0-9]*$/
const BROKEN_ESCAPE = /%(?![0-9a-fA-F]{2})/
const HOST_ESCAPE = /^%(?:25|[89a-fA-F][0-9a-fA-F])$/
const HOST_CHARACTER = /^[A-Za-z0-9!$&'()*+,;=:\]<>"~._-]$/
const FORMAT_CHARACTER = /^\p{Cf}$/u
const ZERO_WIDTH_JOINERS = new Set([0x200c, 0x200d])
const REPLACEMENT_CHARACTER = 0xfffd

function isUnsafeCodePoint(character: string): boolean {
  const codePoint = character.codePointAt(0) ?? REPLACEMENT_CHARACTER
  if (codePoint === REPLACEMENT_CHARACTER || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
    return true
  }
  if (isGoSpace(codePoint) || codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f)) {
    return true
  }
  return FORMAT_CHARACTER.test(character) && !ZERO_WIDTH_JOINERS.has(codePoint)
}

function hasUnsafeCodePoint(value: string): boolean {
  for (const character of value) {
    if (isUnsafeCodePoint(character)) {
      return true
    }
  }
  return false
}

function cutAt(text: string, separator: string): [string, string] {
  const at = text.indexOf(separator)
  return at < 0 ? [text, ''] : [text.slice(0, at), text.slice(at + separator.length)]
}

function withScheme(value: string): string | null {
  if (EXPLICIT_SCHEME.test(value)) {
    return value
  }
  const prefixed = SCHEME_PREFIX.exec(value)
  if (prefixed && !PORT_SUFFIX.test(prefixed[1] ?? '')) {
    return null
  }
  return `https://${value}`
}

function isValidHostText(host: string): boolean {
  for (let i = 0; i < host.length; i += 1) {
    const character = host.charAt(i)
    if (character === '%') {
      if (!HOST_ESCAPE.test(host.slice(i, i + 3))) {
        return false
      }
      i += 2
    } else if (character.charCodeAt(0) < 0x80 && !HOST_CHARACTER.test(character)) {
      return false
    }
  }
  return true
}

function hasPlausibleHost(authority: string): boolean {
  if (authority.includes('@') || authority.includes('[')) {
    return false
  }
  const portAt = authority.lastIndexOf(':')
  if (portAt >= 0 && !OPTIONAL_PORT.test(authority.slice(portAt))) {
    return false
  }
  if (!isValidHostText(authority)) {
    return false
  }
  const hostname = portAt >= 0 ? authority.slice(0, portAt) : authority
  const host = hostname.endsWith('.') ? hostname.slice(0, -1) : hostname
  return host.includes('.') && !host.includes(':') && host.split('.').every((label) => label !== '')
}

export function isLikelyUrl(value: string): boolean {
  const trimmed = trimGoSpace(value)
  if (trimmed === '' || hasUnsafeCodePoint(trimmed)) {
    return false
  }
  const candidate = withScheme(trimmed)
  if (candidate === null) {
    return false
  }
  const schemeEnd = candidate.indexOf('://')
  const scheme = candidate.slice(0, schemeEnd).toLowerCase()
  if (scheme !== 'http' && scheme !== 'https') {
    return false
  }
  const [beforeFragment, fragment] = cutAt(candidate.slice(schemeEnd + 3), '#')
  const [beforeQuery] = cutAt(beforeFragment, '?')
  const [authority, path] = cutAt(beforeQuery, '/')
  if (BROKEN_ESCAPE.test(fragment) || BROKEN_ESCAPE.test(path)) {
    return false
  }
  return hasPlausibleHost(authority)
}
